<script setup>
import { ref, computed } from 'vue'
import _ from 'lodash'
import PaywallCard from './PaywallCard.vue'
import FormButton from '../Atoms/FormButton.vue'
import IconBase from '../icon/IconBase.vue'
import IconPlus from '../icon/IconPlus.vue'
import IconChevronDown from '../icon/IconChevronDown.vue'
import IconChevronUpDown from '../icon/IconChevronUpDown.vue'
import IconCheckmark from '../icon/IconCheckmark.vue'
import stripeIcon from '@/assets/images/stripe-icon.png'
import DropDownMenu from '../Atoms/DropDownMenu.vue'
import DropDownMenuItem from '../Atoms/DropDownMenuItem.vue'
import Tooltip from '../Atoms/Tooltip.vue'
import PageHeader from '../ui/PageHeader.vue'
import ListingCreateTile from '../ui/ListingCreateTile.vue'
import ListingSearchInput from '../ui/ListingSearchInput.vue'
import ResponsiveToolbar from '../ui/ResponsiveToolbar.vue'
import { planLimitReachedMessage } from '@/Utils/planLimitCopy'
import { usePersistedSort } from '@/Utils/usePersistedSort'

const props = defineProps({
	paywalls: { type: Array, default: () => [] },
	reports: { type: Array, default: () => [] },
	users: { type: Array, default: () => [] },
	stripeId: { type: String, default: '' },
	commission: { type: [Number, String], default: null },
	maxPaywalls: { type: [Number, String], default: 0 },
})

const emit = defineEmits(['open', 'toggle', 'delete', 'create', 'unlink-stripe', 'switch-stripe', 'open-settings'])

const search = ref('')
const sortMenu = ref(null)
const PAYWALL_SORT = ['earnings', 'name', 'recent']
const { sortBy, setSort } = usePersistedSort('castr.sort.paywall', PAYWALL_SORT, 'recent')
const changeSort = (id) => { setSort(id); sortMenu.value?.toggleDropdown() }

const sortOptions = [
	{ id: 'earnings', label: 'Earnings' },
	{ id: 'name', label: 'Name' },
	{ id: 'recent', label: 'Recently added' },
]

const reportFor = (id) => props.reports.find(r => String(r.stream_id) === String(id)) || {}
const earningsOf = (id) => {
	const r = reportFor(id)
	return Number(_.get(r, 'payments_total', 0)) - Number(_.get(r, 'refunds_total', 0))
}
// Users belong to the master and carry an invoices[] array (each invoice has
// the stream_id); there's no top-level u.stream_id. Count distinct users with
// at least one (non-pending) invoice for this paywall's stream.
const customersOf = (id) => _.filter(props.users || [], u =>
	_.some(u.invoices || [], inv => String(inv.stream_id) === String(id))
).length

const planLimit = computed(() => Number(props.maxPaywalls) || 0)

const sortLabel = computed(() => sortOptions.find(o => o.id === sortBy.value)?.label)

const visiblePaywalls = computed(() => {
	let list = [...props.paywalls]
	const q = search.value.trim().toLowerCase()
	if (q) list = list.filter(p => (p.title || '').toLowerCase().includes(q))
	if (sortBy.value === 'earnings') list = _.orderBy(list, p => earningsOf(p.id), 'desc')
	if (sortBy.value === 'name') list = _.orderBy(list, p => (p.title || '').toLowerCase(), 'asc')
	return list
})

const paywallLimit = computed(() => (planLimit.value > 0 ? planLimit.value : null))

const paywallHeaderUsage = computed(() => {
	if (!props.paywalls?.length) return null
	const meta = {
		used: props.paywalls.length,
		label: 'paywalls',
		labelSingular: 'paywall',
	}
	if (paywallLimit.value) meta.limit = paywallLimit.value
	return meta
})

const atPaywallLimit = computed(() => {
	const max = paywallLimit.value
	if (!max) return false
	return (props.paywalls?.length ?? 0) >= max
})

const paywallCreateDisabledTitle = computed(() => {
	if (atPaywallLimit.value) return planLimitReachedMessage('Paywall')
	return ''
})
</script>

<template>
	<div>
		<!-- Header -->
		<PageHeader
			title="Paywall"
			subtitle="Monetize your content with pay-per-view and subscriptions."
			mobile-inline
			:usage="paywallHeaderUsage"
		>
			<DropDownMenu position="right" ref="stripeMenu"
				customClasses="inline-flex items-center gap-2 h-8 !px-2.5 rounded-md text-tiny font-medium leading-none text-nowrap text-surface-text-1 bg-surface-2 ring-1 ring-inset ring-white/[0.06] hover:bg-surface-3 hover:text-white transition-colors">
				<template #toggle-button>
					<span class="relative shrink-0 flex">
						<img :src="stripeIcon" alt="Stripe" class="size-[18px] rounded-[5px]" />
						<span class="absolute -right-0.5 -bottom-0.5 size-2 rounded-full bg-success ring-2 ring-surface-2"></span>
					</span>
					<span>Stripe connected</span>
					<icon-base class="text-surface-7 shrink-0">
						<icon-chevron-down />
					</icon-base>
				</template>
				<template #menu-items>
					<DropDownMenuItem isCustom class="!p-3 w-60 flex flex-col gap-y-2">
						<div class="flex items-center justify-between gap-3">
							<span class="text-tiny text-surface-7">Status</span>
							<span class="inline-flex items-center gap-1.5 text-tiny text-success">
								<span class="size-1.5 rounded-full bg-success"></span> Connected
							</span>
						</div>
						<div v-if="commission" class="flex items-center justify-between gap-3">
							<span class="text-tiny text-surface-7">Commission</span>
							<span class="text-tiny text-surface-9 font-medium">{{ commission }}%</span>
						</div>
						<FormButton type="secondary" size="sm" class="w-full justify-center mt-1"
							@click="emit('switch-stripe')">
							Use a different account
						</FormButton>
						<FormButton type="secondary" size="sm" class="w-full justify-center hover:!text-danger"
							@click="emit('unlink-stripe')">
							Unlink Stripe
						</FormButton>
					</DropDownMenuItem>
				</template>
			</DropDownMenu>
			<Tooltip :active="atPaywallLimit" wrap-trigger :html="true" :hide="1000">
				<FormButton class="gap-1" type="main" size="md" :disabled="atPaywallLimit" @click="emit('create')">
					<icon-base class="mr-2" fill="none">
						<icon-plus />
					</icon-base>
					New <span class="hidden md:inline">Paywall</span>
				</FormButton>
				<template #content>
					<span class="text-surface-8 text-sm" v-html="paywallCreateDisabledTitle" />
				</template>
			</Tooltip>
		</PageHeader>

		<!-- Primary nav: Paywalls | Global Settings -->
		<nav class="inline-flex items-center gap-1 bg-surface-2 ring-1 ring-inset ring-white/[0.06] rounded-lg p-1 mt-4 md:mt-0 mb-4" data-testid="paywall-primary-nav">
			<span class="text-sm font-medium px-3 py-1.5 rounded-md bg-surface-4 text-white shadow-sm">Paywalls</span>
			<FormButton @click="emit('open-settings')"
				custom-classes="text-sm font-medium px-3 py-1.5 rounded-md text-surface-8 hover:text-white hover:bg-surface-3 transition-all">
				Global Settings
			</FormButton>
		</nav>

		<!-- Controls -->
		<ResponsiveToolbar v-if="paywalls.length > 2" class="mb-4">
			<DropDownMenu type="secondary" ref="sortMenu" position="left">
				<template #toggle-button>
					<span class="text-surface-8">Sort by:&nbsp;</span>
					<span class="text-white">{{ sortLabel }}</span>
					<icon-base class="!ml-1 shrink-0 text-surface-8">
						<icon-chevron-up-down />
					</icon-base>
				</template>
				<template #menu-items>
					<DropDownMenuItem v-for="o in sortOptions" :key="o.id" :active="sortBy === o.id" @click="changeSort(o.id)">
						{{ o.label }}
						<icon-base v-if="sortBy === o.id" class="shrink-0 !ml-2 text-surface-7" fill="none">
							<icon-checkmark />
						</icon-base>
					</DropDownMenuItem>
				</template>
			</DropDownMenu>
			<ListingSearchInput v-model="search" />
		</ResponsiveToolbar>

		<!-- Grid -->
		<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 auto-rows-fr gap-4 pb-10">
			<PaywallCard v-for="p in visiblePaywalls" :key="p.id" :paywall="p" :earnings="earningsOf(p.id)"
				:customers="customersOf(p.id)" @open="emit('open', p.id)" @toggle="emit('toggle', p)"
				@delete="emit('delete', p)" />

			<ListingCreateTile
				v-if="!search.trim()"
				label="Paywall"
				:used="paywalls.length"
				:limit="planLimit"
				:limit-reached="atPaywallLimit"
				@create="emit('create')"
			/>

			<!-- No results -->
			<div v-if="search.trim() && visiblePaywalls.length === 0"
				class="col-span-full text-center text-tiny text-surface-6 py-8">
				No paywalls match “{{ search.trim() }}”.
			</div>
		</div>
	</div>
</template>
